import { GoogleAnalytics } from "~/google_analytics/events";
import { EventParameter } from "~/google_analytics/events.enum";

declare global {
  interface Window {
    AndroidInterface?: {
      requestPermission: (type: string) => void;
    };

    webkit?: {
      messageHandlers?: {
        BFLInterface?: {
          postMessage: (msg: any) => void;
        };
      };
    };

    iOSHandler?: (status: PermissionStatus, message?: string) => void;
  }
}

export { };

type PermissionStatus = "success" | "failure";

const PERMISSIONS = ["location", "camera"] as const;
type PermissionType = (typeof PERMISSIONS)[number];

interface PermissionResult {
  type: PermissionType;
  status: PermissionStatus;
  message?: string;
}

const PERMISSION_MAP: Record<PermissionType, PermissionName> = {
  location: "geolocation",
  camera: "camera",
};

export default defineNuxtPlugin(() => {

  const requestPermission = (type: PermissionType): Promise<PermissionResult> => {

    return new Promise(async (resolve) => {

      let resolved = false;
      let handler: ((event: Event) => void) | null = null;
      let timeout: any;

      const cleanup = () => {
        if (handler) {
          window.removeEventListener("BFLPermissionResult", handler);
        }

        if (window.iOSHandler) {
          delete window.iOSHandler;
        }

        clearTimeout(timeout);
      };

      const finish = (status: PermissionStatus, message?: string) => {
        if (resolved) return;

        resolved = true;
        cleanup();

        resolve({ type, status, message });
      };

      const startTimeout = () => {
        timeout = setTimeout(() => {
          finish("failure", "Permission request timeout");
        }, 15000);
      };

      // ---------------- ANDROID ----------------

      if (window.AndroidInterface?.requestPermission) {

        handler = (event: Event) => {
          const customEvent = event as CustomEvent<PermissionResult>;

          if (
            customEvent.detail?.type?.toLowerCase() === type.toLowerCase()
          ) {
            finish(customEvent.detail.status, customEvent.detail.message);
          }
        };

        window.addEventListener("BFLPermissionResult", handler);

        startTimeout();

        window.AndroidInterface.requestPermission(type);

        return;
      }

      // ---------------- iOS ----------------

      if (window.webkit?.messageHandlers?.BFLInterface) {

        window.iOSHandler = (status, message) => {
          finish(status, message);
        };

        startTimeout();

        window.webkit.messageHandlers.BFLInterface.postMessage({
          action: "requestPermission",
          type
        });

        return;
      }

      // ---------------- WEB ----------------

      try {

        if (!("permissions" in navigator)) {
          return finish("failure", "Permissions API not supported");
        }

        const permissionName = PERMISSION_MAP[type];

        const { state } = await navigator.permissions.query({
          name: permissionName
        });

        switch (state) {

          case "granted":
            finish("success");
            break;

          case "prompt":

            if (type === "location") {
              GoogleAnalytics.locationEvent({
                [EventParameter.ACTION]: 'location_permission_prompt',
                [EventParameter.LOCATION_AVAILABLE]: "no",
                [EventParameter.PROMPT_TYPE]: "browser-native",
                [EventParameter.LATITUDE]: 0,
                [EventParameter.LONGITUDE]: 0,
                [EventParameter.ADDRESS]: 'none',
                [EventParameter.REASON]: 'no_location_permission',

              });
              navigator.geolocation.getCurrentPosition(
                () => finish("success"),
                (err) => finish("failure", err.message),
                {
                  enableHighAccuracy: false,
                  timeout: 15000,
                  maximumAge: 60000
                }
              );

            } else if (type === "camera") {

              try {
                await navigator.mediaDevices.getUserMedia({ video: true });
                finish("success");
              } catch (err: any) {
                finish("failure", err?.message);
              }

            }

            break;

          case "denied":
            finish("failure", `${type} permission denied`);
            break;

          default:
            finish("failure", `Unknown ${type} permission state`);
        }

      } catch (err: any) {

        finish("failure", err?.message || "Permission check failed");

      }

    });
  };

  // ---------------- SHORTCUT FUNCTIONS ----------------

  const shortcuts = Object.fromEntries(
    PERMISSIONS.map((perm) => [
      `call${perm.charAt(0).toUpperCase() + perm.slice(1)}PermissionFromBFLApp`,
      () => requestPermission(perm)
    ])
  );

  return {
    provide: {
      callPermissionFromBFLApp: requestPermission,
      ...shortcuts
    }
  };

});